// Pure predicate deciding which Phantom (MCP) orders still need status polling.
// Extracted from PhantomOrderSyncBridge so it can be unit-tested without a DOM.
//
// Coach maps the mid-lifecycle Phantom/Deliverect statuses (submitted_to_pos,
// accepted, preparing, ready) to `confirmed`; `completed` maps to `completed`.
// Deliverect is ASYNC: an order sits at submitted_to_pos until the POS returns
// Accepted (20) or Failed (120/121). If we stop polling the moment an order
// leaves `pending_confirmation`, a late POS failure never surfaces to the
// coach. So we keep polling through the pre-acceptance window and stop once the
// POS has the order (accepted+) or it reaches a terminal state. The eventual
// confirmed → completed transition is NOT this poller's job — the server-side
// completion sweep (server/services/orderCompletionSweep.mjs) handles orders
// whose fulfillment time has passed, so it works with no tab open.

// Raw Phantom provider statuses that mean "the POS has it — it won't
// spontaneously fail", so we can stop the aggressive poll.
export const PHANTOM_ACCEPTED_PROVIDER_STATUSES = new Set([
  'accepted',
  'preparing',
  'ready',
  'completed',
]);

export const PHANTOM_TERMINAL_STATUSES = new Set(['failed', 'cancelled', 'canceled', 'completed']);

// The server's answer (status route `provider_sync`, tracking route 409 `code`)
// when an order is on a coach's own DoorDash account and MealOps has no
// working sign-in for it. Not an outage: it reads the same until that coach
// reconnects DoorDash, and a not-on-account read is still a DoorDash call on
// their account, so pollers must not keep asking.
export const DOORDASH_ACCOUNT_REFUSALS = new Set(['doordash_reconnect_required', 'doordash_order_not_on_account']);

export const normalizeText = (value) => {
  if (value == null) return null;
  const text = String(value).trim();
  return text || null;
};

export const isMcpOrder = (order) => {
  const restaurant = order?.restaurant || null;
  const orderIntent =
    order?.request_payload && typeof order.request_payload === 'object'
      ? order.request_payload.order_intent
      : null;

  // DoorDash marketplace orders are Phantom orders too (status via Phantom's
  // get_order_status); they now persist the brand ('doordash') instead of the
  // 'mcp' transport, so match both generations of rows.
  const apiSource = normalizeText(order?.api_source);
  return (
    apiSource === 'mcp' ||
    apiSource === 'doordash' ||
    restaurant?.integration_type === 'mcp_server' ||
    restaurant?.provider_name === 'mcp' ||
    orderIntent?.restaurant_integration_type === 'mcp_server' ||
    orderIntent?.restaurant_provider_name === 'mcp' ||
    orderIntent?.marketplace_provider === 'mcp' ||
    orderIntent?.marketplace_provider === 'doordash' ||
    orderIntent?.order_execution_adapter === 'mcp'
  );
};

export const phantomProviderStatusOf = (order) => {
  const raw = order?.response_payload;
  const value = raw && typeof raw === 'object' ? raw.phantom_provider_status : null;
  return normalizeText(value)?.toLowerCase() || '';
};

// scheduled_date is a timestamptz in the DB ("2026-07-16T22:55:00+00:00");
// older rows/fixtures may carry a bare calendar date ("2026-07-16"). A
// confirmed order usually gets injected to the POS (and thus accepted/failed)
// around its fulfillment day, so the FAST poll only covers orders due on or
// after that day — a scheduled order days out shouldn't poll every 15s.
// (The bridge's slow lane still sweeps not-yet-due orders occasionally, since
// a DMA operator can accept a scheduled order days early.) `now` is injectable
// for testing.
//
// NOTE: do NOT string-compare scheduled_date against a YYYY-MM-DD string — a
// timestamptz always sorts greater than its own date prefix, which silently
// deferred polling for EVERY scheduled order (badge stuck at 'submitted').
export const isDueForAcceptancePolling = (order, now = new Date()) => {
  const scheduled = normalizeText(order?.scheduled_date);
  if (!scheduled) return true; // ASAP / unknown → active now
  const parsed = new Date(scheduled);
  if (Number.isNaN(parsed.getTime())) return true; // unparseable → fail open, poll
  const startOfScheduledDay = new Date(parsed.getFullYear(), parsed.getMonth(), parsed.getDate());
  return now.getTime() >= startOfScheduledDay.getTime();
};

export const isPendingPhantomOrder = (order, now = new Date(), { includeNotYetDue = false } = {}) => {
  if (!isMcpOrder(order)) return false;
  const status = normalizeText(order?.order_status);
  if (!status || PHANTOM_TERMINAL_STATUSES.has(status)) return false;
  // Awaiting the coach/manager confirmation step — always poll.
  if (status === 'pending_confirmation') return true;
  // Placed but pre-acceptance ('submitted'), or 'confirmed' whose provider
  // status hasn't caught up yet: keep polling through the async pre-acceptance
  // window so acceptance promotes it (submitted→confirmed) and a late POS
  // failure surfaces as `failed`. Once the provider status is accepted+, the
  // check below returns false and polling stops. `includeNotYetDue` is the
  // bridge's slow lane: far-future scheduled orders skip the 15s cadence but
  // still get swept occasionally, because the restaurant can accept a
  // scheduled order in the DMA days before fulfillment (and acceptance now
  // drives capture + payout, so Coach should reflect it promptly).
  if (status === 'submitted' || status === 'confirmed') {
    return (
      !PHANTOM_ACCEPTED_PROVIDER_STATUSES.has(phantomProviderStatusOf(order)) &&
      (includeNotYetDue || isDueForAcceptancePolling(order, now))
    );
  }
  return false;
};
